import { useQuery } from '@tanstack/react-query'
import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'
import {
  CARD_OVERVIEW_STATS_DEMO,
  type CardOverviewStatsDemo,
} from 'src/feature/CardOverviewStats/cardOverviewStatsDemoData'
import { toIsoDate } from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.mapping'
import type {
  CardManagementNextPaymentDueStatus,
  CardManagementStatementProvenance,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { isCardManagementActionAvailable } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * Per-group load state, mirroring `useCardDetailHeroData`'s vocabulary so the
 * two card-management seams read the same way.
 *
 * Only these three, because only these three can happen. `error` and
 * `unavailable` were modelled here until the demo fallback swallowed both, and
 * carrying them cost more than it explained: the type promised a distinction
 * the implementation did not make, and consumers branched on states they could
 * never see. Reinstate them alongside the surrogate limits endpoint, when there
 * is something that can actually produce them — see the TODO on the denial
 * branch in `limitsState` below.
 */
export type CardOverviewStatsGroupState =
  | 'loading'
  | 'ready'
  /**
   * Real limits could not be read, so `limits` holds FABRICATED figures from
   * `CARD_OVERVIEW_STATS_DEMO`. Deliberately collapses a denied capability and
   * a failed request into one state: with a stand-in already on screen, "you
   * don't have permission" contradicts the numbers beside it.
   *
   * NOTHING marks these as fabricated on screen — read `CardOverviewStats`' doc
   * comment before widening where this renders. Remove once the surrogate
   * limits endpoint lands.
   */
  | 'demo'

export type CardOverviewLimits = {
  /** Balance drawn on the current cycle. */
  balance: Money
  /** The card's permanent spend limit. */
  limit: Money
  /**
   * Balance as a percentage of the limit, 0–100.
   *
   * `undefined` when the limit is zero or negative — utilization against no
   * limit is undefined, NOT zero, and rendering 0% there would read as "plenty
   * of room" on a card that has none.
   */
  utilizationPercent: number | undefined
}

/**
 * Per-group load state for the payment and billing-cycle groups.
 *
 * Unlike `CardOverviewStatsGroupState`, these two have no demo fallback: a
 * denied capability is a settled answer about the card (`unavailable`), not a
 * stand-in for one, and is kept distinct from a failed request (`error`). This
 * is the vocabulary the `TODO(surrogate-limits)` comment above says the limits
 * group still owes once its demo fallback is retired.
 */
export type CardOverviewStatsSourceState =
  | 'loading'
  | 'ready'
  | 'unavailable'
  | 'error'

export type CardOverviewPaymentDue = {
  amount: Money
  /** LocalDate, `"YYYY-MM-DD"`. */
  dueDate: string
  status: CardManagementNextPaymentDueStatus
}

export type CardOverviewBillingCycle = {
  /** LocalDate, `"YYYY-MM-DD"` — the statement-close date. */
  periodEnd: string
}

export type CardOverviewStatsData = {
  /**
   * `undefined` only while `limitsState` is `loading`. Real when `ready`,
   * FABRICATED when `demo` — check the state before trusting it.
   */
  limits: CardOverviewLimits | undefined
  limitsState: CardOverviewStatsGroupState
  /**
   * `undefined` unless `paymentState` is `ready`. `null` is itself a real
   * `ready` answer — "nothing currently due" — and must not be treated as a
   * gap; see `CardManagementPaymentSummaryResponse`.
   */
  payment: CardOverviewPaymentDue | null | undefined
  paymentState: CardOverviewStatsSourceState
  /** Set only once `paymentState` is `ready`. */
  paymentProvenance: CardManagementStatementProvenance | undefined
  /** `undefined` unless `billingCycleState` is `ready`. */
  billingCycle: CardOverviewBillingCycle | undefined
  billingCycleState: CardOverviewStatsSourceState
  /** Set only once `billingCycleState` is `ready`. */
  billingCycleProvenance: CardManagementStatementProvenance | undefined
  /**
   * FABRICATED. Named `demo` rather than folded into plausible-sounding groups
   * so no call site can consume it without seeing what it is. See
   * `cardOverviewStatsDemoData.ts` for what each field is blocked on.
   */
  demo: CardOverviewStatsDemo
}

/** Exported for tests; not part of the seam's contract. */
export const utilizationPercentOf = (
  balanceMinor: number,
  limitMinor: number,
): number | undefined =>
  limitMinor > 0 ? (balanceMinor / limitMinor) * 100 : undefined

/**
 * Shared state derivation for the payment and billing-cycle groups. Unlike
 * `limitsState`, a denied capability resolves to `unavailable` and a failed
 * request resolves to `error` — there is no demo fallback to collapse them
 * into, so the two stay distinct.
 */
const sourceStateOf = (
  capabilitiesFailed: boolean,
  allowed: boolean | undefined,
  query: { data: unknown; isError: boolean },
): CardOverviewStatsSourceState => {
  if (capabilitiesFailed) return 'error'
  if (allowed === undefined) return 'loading'
  if (!allowed) return 'unavailable'
  if (query.data !== undefined) return 'ready'
  return query.isError ? 'error' : 'loading'
}

/**
 * Single data seam for the card Overview stats roll-up (EX-40632).
 *
 * The roll-up's sections MUST read through this hook rather than importing
 * `CARD_OVERVIEW_STATS_DEMO` directly, so wiring a real endpoint touches this
 * file and nothing else.
 *
 * Grouped by SOURCE, not by consumer: `limits` is a real, independently fetched
 * endpoint with its own failure window, and `demo` is everything with no
 * endpoint behind it. Roughly half of what this roll-up displays is fabricated —
 * see the demo module for the per-field blockers.
 *
 * NOT here, deliberately: the virtual-card and budget counts. Those live in the
 * legacy per-credit-card statistics family, whose selectors are route-coupled
 * (`selectCreditCardIdFromRoute`), and features must not read routing. They also
 * cannot be re-fetched here safely — the paginated credit-card query dispatches
 * `GET_CREDIT_CARDS_INIT`, which flips `fetchingCreditCards` in four unrelated
 * reducers. The mounting page passes them as props instead, exactly as the
 * legacy `AccountOverview` already does.
 *
 * @param creditCardId - The card whose limits to read. A required argument
 *   rather than something this hook resolves: the roll-up is a feature, and the
 *   page owns the URL.
 *
 *   Required in the type as well as in spirit. An absent id disables the
 *   capability query, and a disabled query neither resolves nor errors — so
 *   `limitsAllowed` would stay `undefined` and this hook would report `loading`
 *   with no terminal state, holding `aria-busy` on the roll-up forever. There
 *   is no sensible answer to give without an id, so the type refuses the
 *   question.
 */
export const useCardOverviewStatsData = (
  creditCardId: string,
): CardOverviewStatsData => {
  // `retry: false` comes from the factory and is deliberate — a denied
  // capability is an answer, not a flake.
  const capabilitiesQuery = useQuery(
    physicalCardManagementQueries.capabilities(creditCardId),
  )

  // Deliberately keyed off `capabilitiesQuery.data` rather than passing the
  // capabilities straight through: the shared helper collapses "no verdict yet"
  // and "verdict says no" into the same `false`, and this seam needs to tell
  // them apart — `undefined` is what holds `limitsState` at `loading` instead of
  // flashing a fabricated balance before the real one arrives.
  const limitsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_LIMIT',
      )
    : undefined

  const limitsQuery = useQuery({
    ...physicalCardManagementQueries.limits(creditCardId),
    // A spread override REPLACES the factory's arg-presence guard rather than
    // merging with it, so it is re-stated here. Declarative `enabled` rather
    // than an effect: a denied capability must never fire a request the backend
    // will reject.
    enabled: Boolean(creditCardId) && limitsAllowed === true,
  })

  const limitsState = ((): CardOverviewStatsGroupState => {
    // `loading` is preserved rather than short-circuited to `demo`: a card whose
    // real limits are one tick away should not flash a fabricated balance first.
    if (capabilitiesQuery.isError) return 'demo'
    if (limitsAllowed === undefined) return 'loading'
    // TODO(surrogate-limits): this branch becomes `unavailable`, NOT `demo`.
    // A denied `READ_PHYSICAL_CARD_LIMIT` is a settled answer about this card,
    // not a stand-in for one — it only shares a state with a failed request
    // because there are fabricated figures to show either way. Whoever deletes
    // `CARD_OVERVIEW_STATS_DEMO` must split this out in the same change, or a
    // permission-denied user silently keeps receiving invented numbers after
    // the demo module is supposedly gone.
    if (!limitsAllowed) return 'demo'
    if (limitsQuery.data !== undefined) return 'ready'
    return limitsQuery.isError ? 'demo' : 'loading'
  })()

  const limitsResponse = limitsQuery.data

  const demoLimits: CardOverviewLimits = {
    balance: CARD_OVERVIEW_STATS_DEMO.balance,
    limit: CARD_OVERVIEW_STATS_DEMO.limit,
    utilizationPercent: utilizationPercentOf(
      CARD_OVERVIEW_STATS_DEMO.balance.amountMinor,
      CARD_OVERVIEW_STATS_DEMO.limit.amountMinor,
    ),
  }

  // Same "no verdict yet" vs "verdict says no" distinction as `limitsAllowed`
  // above, so a pending capability check cannot flash `unavailable` before the
  // real verdict arrives.
  const paymentsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_PAYMENTS',
      )
    : undefined

  const paymentSummaryQuery = useQuery({
    ...physicalCardManagementQueries.paymentSummary(creditCardId),
    // Restated for the same reason as `limitsQuery.enabled`: a spread override
    // replaces the factory's arg-presence guard rather than merging with it.
    enabled: Boolean(creditCardId) && paymentsAllowed === true,
  })

  const paymentState = sourceStateOf(
    capabilitiesQuery.isError,
    paymentsAllowed,
    paymentSummaryQuery,
  )

  const paymentResponse = paymentSummaryQuery.data

  // `null` is `ready`'s own answer — "nothing currently due" — never a gap.
  const payment: CardOverviewPaymentDue | null | undefined =
    paymentState !== 'ready' || paymentResponse === undefined
      ? undefined
      : paymentResponse.nextPaymentDue === null
        ? null
        : {
            amount: {
              amountMinor: paymentResponse.nextPaymentDue.amountDue,
              // The payment's OWN currency, not the card's limits currency.
              currency: paymentResponse.nextPaymentDue.currency,
            },
            // The wire carries `[year, month, day]`; the hook is the seam that
            // normalizes it to the ISO string the stat formatters take.
            dueDate: toIsoDate(paymentResponse.nextPaymentDue.dueDate),
            status: paymentResponse.nextPaymentDue.status,
          }

  const paymentProvenance =
    paymentState === 'ready' ? paymentResponse?.provenance : undefined

  const statementsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_STATEMENTS',
      )
    : undefined

  const currentCycleQuery = useQuery({
    ...physicalCardManagementQueries.currentCycle(creditCardId),
    enabled: Boolean(creditCardId) && statementsAllowed === true,
  })

  const billingCycleState = sourceStateOf(
    capabilitiesQuery.isError,
    statementsAllowed,
    currentCycleQuery,
  )

  const currentCycleResponse = currentCycleQuery.data

  const billingCycle: CardOverviewBillingCycle | undefined =
    billingCycleState === 'ready' && currentCycleResponse !== undefined
      ? { periodEnd: toIsoDate(currentCycleResponse.periodEnd) }
      : undefined

  const billingCycleProvenance =
    billingCycleState === 'ready'
      ? currentCycleResponse?.provenance
      : undefined

  return {
    limits:
      limitsState === 'demo'
        ? demoLimits
        : limitsState === 'ready' && limitsResponse !== undefined
          ? {
              balance: {
                amountMinor: limitsResponse.currentBalance,
                currency: limitsResponse.currency,
              },
              limit: {
                amountMinor: limitsResponse.permanentLimit,
                currency: limitsResponse.currency,
              },
              utilizationPercent: utilizationPercentOf(
                limitsResponse.currentBalance,
                limitsResponse.permanentLimit,
              ),
            }
          : undefined,
    limitsState,
    payment,
    paymentState,
    paymentProvenance,
    billingCycle,
    billingCycleState,
    billingCycleProvenance,
    demo: CARD_OVERVIEW_STATS_DEMO,
  }
}
